import { MONEY_SCALE, type InvoiceDetailDto, describe } from "@hisense/shared";
import { invoiceDocumentFacts } from "./invoiceDocument";
import { printHtml } from "./printWindow";

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!);

const na = (s: string | null | undefined, empty = "N/A") => {
  const v = (s ?? "").trim();
  return v ? esc(v) : empty;
};

const printDate = (ms: number | null | undefined) => {
  if (!ms) return "N/A";
  return new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const major4 = (minor: number) => (minor / MONEY_SCALE).toFixed(4);
const major2 = (minor: number) => (minor / MONEY_SCALE).toFixed(2);

/**
 * Commercial invoice print — layout matches Invoice.js generatePrintContent:
 * INVOICE title, INV+PI numbers, 9-column black table (no style/MOQ/EXW extras),
 * SUBTOTAL / UPCHARGE / LESS / GRAND TOTAL, remittance + bank block.
 */
export function buildInvoiceHtml(inv: InvoiceDetailDto) {
  // Các con số đến từ MỘT chỗ duy nhất, dùng chung với CSV và Excel: trước
  // 15/09/2026 mỗi nơi tự tính và CSV làm tròn khác, lệch một xu.
  const { lineSubtotal, base, discountPct, less, grand, recomputed: calculated } = invoiceDocumentFacts(inv);
  // Logo của CHÍNH pháp nhân thu tiền trên chứng từ này; chưa đặt logo riêng
  // thì lùi về logo Hisense đi kèm ứng dụng. Trước đây luôn cắm cứng tệp đó,
  // nên chứng từ do pháp nhân khác phát hành vẫn in ra logo Hisense.
  const logoSrc = inv.paymentBank?.logoUrl || `${window.location.origin}/brand/hisense-logo.webp`;
  const shipContact = inv.consigneeSnapshot || inv.shipTo;
  const bank = inv.paymentBank;

  const itemRows = inv.items
    .map((it, index) => {
      const productId = it.productCode || (it.productId ? it.productId.slice(-8) : "-");
      const zebra = index % 2 === 0 ? "white" : "#f5f5f5";
      return `<tr style="background-color:${zebra};">
        <td class="c mono b">${esc(productId)}</td>
        <td class="c muted">${na(it.subRef, "-")}</td>
        <td>${esc(describe(it, inv.printLanguage))}${it.lotNumber?`<br><span class="mono">LOT: ${esc(it.lotNumber)}</span>`:""}</td>
        <td class="c">${na(it.color, "-")}</td>
        <td class="c">${na(it.size, "-")}</td>
        <td class="c b">${it.quantity}</td>
        <td class="c">${na(it.unit, "-")}</td>
        <td class="c b">${esc(inv.currency)} ${major4(it.unitPrice)}</td>
        <td class="c b">${esc(inv.currency)} ${major4(it.amount)}</td>
      </tr>`;
    })
    .join("");

  const bankBlock = bank
    ? `<div style="margin-top:15px;">
        <div style="font-weight:bold;margin-bottom:10px;">PAYMENT INFORMATION:</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:15px;font-size:11px;">
          <div>
            <p><strong>Beneficiary:</strong> ${na(bank.beneficiary)}</p>
            <p><strong>Beneficiary Bank:</strong> ${na(bank.beneficiaryBank)}</p>
            <p><strong>Bank Address:</strong> ${na(bank.bankAddress)}</p>
          </div>
          <div>
            <p><strong>Account No (HKD):</strong> ${na(bank.accountNumberHkd)}</p>
            <p><strong>Account No (USD):</strong> ${na(bank.accountNumberUsd)}</p>
            ${
              // RMB chỉ in khi công ty có số: HKD/USD in "N/A" theo mẫu hệ cũ, còn RMB là
              // trường mới — in "N/A" trên mọi chứng từ của công ty không nhận RMB chỉ gây nhiễu.
              bank.accountNumberRmb?.trim() ? `<p><strong>Account No (RMB):</strong> ${na(bank.accountNumberRmb)}</p>` : ""
            }
            <p><strong>Swift Code:</strong> <span class="mono b">${na(bank.swiftCode)}</span></p>
          </div>
        </div>
      </div>`
    : "";

  const remarks = inv.remarks?.trim()
    ? `<div class="remarks-section">
        <div class="remarks-title">REMARKS:</div>
        <p style="margin:0;line-height:1.5;white-space:pre-wrap;">${esc(inv.remarks)}</p>
      </div>`
    : "";

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice ${esc(inv.invNo)} - Hisense Button</title>
  <style>
    @page { margin: 15mm; size: A4; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, sans-serif; margin: 0; padding: 0; color: #000;
      line-height: 1.4; font-size: 12px; background: white;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .page { width: 100%; max-width: 210mm; margin: 0 auto; }
    .mono { font-family: "Courier New", monospace; }
    .c { text-align: center; } .b { font-weight: bold; } .muted { color: #666; }
    p { margin: 5px 0; }
    table.items { table-layout:fixed; overflow-wrap:anywhere; width: 100%; border-collapse: collapse; border: 2px solid #000; font-size: 11px; }
    table.items th, table.items td { border: 1px solid #000; padding: 8px 6px; vertical-align: top; }
    table.items th {
      background: #000 !important; color: #fff !important; font-weight: bold;
      padding: 10px 6px; font-size: 11px;
      -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
    }
    .remarks-section { margin: 20px 0; padding: 15px; border: 1px solid #000; }
    .remarks-title { font-weight: bold; margin-bottom: 10px; }
    .terms-section { margin: 20px 0; padding: 15px; border: 2px solid #000; }
    .terms-title { font-weight: bold; margin-bottom: 15px; font-size: 14px; }
    .remittance-notice { background: #f0f0f0; padding: 10px; margin: 10px 0; border-left: 4px solid #000; font-weight: bold; }
    .box-label { margin:0 0 8px;font-size:14px;font-weight:bold;border-bottom:1px solid #000;padding-bottom:3px; }
    .box { border:1px solid #000;padding:12px;min-height:60px; }
    @media print {
      body { margin: 0; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      table.items th { background: #000 !important; color: #fff !important; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;padding-bottom:15px;">
      <div style="flex:1;">
        <img src="${esc(logoSrc)}" alt="Hisense Button Co., Ltd"
          style="max-height:80px;max-width:300px;object-fit:contain;"
          onerror="this.style.display='none';this.nextElementSibling.style.display='block';" />
        <div style="display:none;padding:15px;border:2px solid #000;max-width:300px;">
          <h2 style="margin:0;font-size:20px;">Hisense Button Co., Ltd</h2>
          <p style="margin:3px 0 0 0;font-size:12px;">Button Import &amp; Export Specialists</p>
        </div>
      </div>
      <div style="text-align:right;margin-left:20px;">
        <h1 style="margin:0;color:#2563eb;font-size:26px;font-weight:bold;letter-spacing:2px;">INVOICE</h1>
        <p style="margin:10px 0 2px;font-size:14px;">${inv.pProcess?"<strong>P-Process</strong><br>":""}<strong>INV No:</strong> <span class="mono b">${esc(inv.invNo)}</span></p>
        <p style="margin:2px 0;font-size:14px;"><strong>PI No:</strong> <span class="mono b">${na(inv.piNo)}</span></p>
      </div>
    </div>

    <hr style="border:none;border-top:2px solid #000;margin:20px 0;">

    <div style="display:flex;gap:20px;margin-bottom:25px;">
      <div style="flex:0 0 60%;">
        <div style="display:flex;gap:20px;margin-bottom:20px;">
          <div style="flex:1;">
            <h3 class="box-label">BILL TO:</h3>
            <div class="box">${na(inv.billToName, "Not specified")}</div>
          </div>
          <div style="flex:1;">
            <h3 class="box-label">SHIPPING MARKS:</h3>
            <div class="box">${na(inv.shippingMarks, "Not specified")}</div>
          </div>
        </div>
        <h3 class="box-label">SHIP TO (Consignee):</h3>
        <div class="box">
          ${/* Không có người nhận riêng thì hàng giao cho chính người mua —
                đó là cách hệ thống cũ in, và 67/199 hoá đơn rơi vào trường hợp
                này. Bỏ trắng ô SHIP TO trên chứng từ thương mại là để hải quan
                và người vận chuyển không biết giao cho ai. */""}
          ${(inv.consigneeName || inv.billToName) ? `<p style="margin:0 0 6px;font-weight:bold;font-size:13px;">${esc(inv.consigneeName || inv.billToName || "")}</p>` : ""}
          <p style="margin:0;white-space:pre-line;">${na(shipContact, "Not specified")}</p>
        </div>
      </div>
      <div style="flex:0 0 40%;border:1px solid #000;align-self:flex-start;font-size:9px;line-height:1.4;padding:8px 15px;">
        <p style="margin:0 0 5px;"><strong>Date:</strong> ${printDate(inv.date ?? inv.createdAt)}</p>
        <p><strong>Customer PO No:</strong> ${na(inv.customerPoNo)}</p>
        <p><strong>Customer Dept:</strong> <strong>${na(inv.customerDept)}</strong></p>
        <p><strong>Delivery Date:</strong> ${printDate(inv.deliveryDate)}</p>
        <p><strong>Shipping Mode:</strong> ${na(inv.shippingMode)}</p>
        <p><strong>Port of Loading:</strong> ${na(inv.portOfLoading)}</p>
        <p><strong>Country of Origin:</strong> ${na(inv.countryOfOrigin)}</p>
        <p><strong>D.N.No:</strong> ${na(inv.dnNo)}</p>
        <p><strong>Payment Terms:</strong> ${na(inv.paymentTerms)}</p>
        <p><strong>Currency:</strong> <strong>${esc(inv.currency)}</strong></p>
        <p style="margin:5px 0 0;"><strong>Resp. By:</strong> ${na(inv.respBy)}</p>
      </div>
    </div>

    <table class="items">
      <thead>
        <tr>
          <th>Product ID</th>
          <th>Sub Ref</th>
          <th style="text-align:left;">Description</th>
          <th>Color</th>
          <th>Size</th>
          <th>Quantity</th>
          <th>Unit</th>
          <th>Unit Price</th>
          <th>Total Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
        <tr style="background:#f9f9f9;">
          <td colspan="8" style="text-align:right;font-weight:bold;">SUBTOTAL:</td>
          <td class="c b">${esc(inv.currency)} ${major4(lineSubtotal)}</td>
        </tr>
        <tr style="background:#f9f9f9;">
          <td colspan="8" style="text-align:right;font-weight:bold;">UPCHARGE:</td>
          <td class="c b">${esc(inv.currency)} ${major4(inv.upcharge ?? 0)}</td>
        </tr>
        <tr><td colspan="8" style="text-align:right;">ROUNDED BASE:</td><td class="c b">${esc(inv.currency)} ${major2(base)}</td></tr>
        <tr style="background:#f9f9f9;">
          <td colspan="8" style="text-align:right;font-weight:bold;">LESS (${discountPct.toFixed(2)}%):</td>
          <td class="c b">-${esc(inv.currency)} ${major2(less)}</td>
        </tr>
        <tr style="background:#f0f0f0;border-top:2px solid #000;">
          <td colspan="8" style="text-align:right;font-weight:bold;font-size:14px;padding:12px 8px;">GRAND TOTAL:</td>
          <td class="c b" style="font-size:16px;background:white;padding:12px 8px;">${esc(inv.currency)} ${major2(grand)}</td>
        </tr>
      </tbody>
    </table>

    ${grand!==calculated?`<p>Stored document total differs from the calculated total (${esc(inv.currency)} ${major2(calculated)}). Review before issuing.</p>`:""}
    ${remarks}

    <div class="terms-section">
      <div class="terms-title">TERMS &amp; CONDITIONS:</div>
      <div class="remittance-notice">
        ⚠️ ALL REMITTANCE CHARGES FROM OUTSIDE OF HONG KONG IS NOT ALLOWED TO DEDUCT FROM OUR INVOICE AMOUNT
      </div>
      ${bankBlock}
    </div>

    <div style="text-align:center;padding-top:20px;border-top:1px solid #000;font-size:10px;">
      <p style="margin:0;font-style:italic;">Thank you for your business!</p>
      <p style="margin:5px 0 0;">Generated on ${esc(new Date().toLocaleString())} | Hisense Button Export Import Management System</p>
    </div>
  </div>
</body>
</html>`;

  return html;
}

export function printInvoice(inv: InvoiceDetailDto){printHtml(buildInvoiceHtml(inv));}
